import type { Currency } from '@ps/web-core-enums';
import type { TranslationTag, Values } from '@ps/web-core-intl';
import { Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import { camelCase } from 'lodash';
import type { ReactNode } from 'react';
import React from 'react';
import { FormattedNumber } from 'react-intl';

import type { FeatureVariant } from '@swf/casino';
import CurrencyAmount from '@swf/components/CurrencyAmount/CurrencyAmount';

import styles from './FeatureDetails.scss';

export enum FormatType {
  String = 'string',
  Currency = 'currency',
  Percentage = 'percent',
  Translation = 'translation'
}

export const BLANK_VALUE = '—';

export const isBlankValue = (value: Props['value']) => BLANK_VALUE === value;

export interface Props {
  format: string;
  className?: string;
  title: TranslationTag;
  value: string | number | TranslationTag[];
  variant?: FeatureVariant[];
  currency?: Currency;
  inline?: boolean;
  currencyDigits?: number;
}

const FeatureDetails: React.FC<Props> = ({
  currencyDigits = 2,
  className,
  title,
  value,
  format,
  currency,
  variant = [],
  inline
}) => (
  <div
    className={classNames(
      variant?.map((v) => styles[camelCase(v) as keyof typeof styles]),
      className
    )}
  >
    <span className={classNames(styles.infoDescription, inline && styles.inline)}>
      {inline ? (
        <Translation
          {...title}
          values={
            {
              0: isBlankValue(value) ? (
                value
              ) : (
                <CurrencyAmount currency={currency} value={parseFloat(value as string)} />
              )
            } as Values
          }
        />
      ) : (
        <Translation {...title} />
      )}
      {!inline && ': '}
    </span>
    {!inline && (
      <span
        className={classNames(
          styles.infoValue,
          variant?.map((v) => styles[camelCase(v) as keyof typeof styles])
        )}
      >
        {(format === FormatType.String || isBlankValue(value)) && (value as ReactNode)}
        {format === FormatType.Translation &&
          (value as TranslationTag[])
            .filter((theObj) => !!theObj)
            .map((theObj, i) => [i > 0 && ', ', <Translation key={i} {...theObj} />])}
        {format === FormatType.Percentage && (
          <FormattedNumber
            style="percent"
            maximumFractionDigits={2}
            value={(value as number) / 100}
          />
        )}
        {format === FormatType.Currency && !isBlankValue(value) && (
          <CurrencyAmount
            value={parseFloat(value as string)}
            currency={currency}
            maximumFractionDigits={currencyDigits}
            minimumFractionDigits={currencyDigits}
          />
        )}
      </span>
    )}
  </div>
);

export default FeatureDetails;
